import { useState, useEffect } from "react";
import { useLocalization } from "@fluent/react";
import {
  Drawer,
  Button,
  Row,
  Avatar,
  Tooltip,
  Chip,
  formatNumber,
  ContentCard,
} from "@narmi/design_system";
import Role, { MODEL_STR } from "byzantine/src/Role";
import OrganizationUser from "byzantine/src/OrganizationUser";
import {
  getUsersForRole,
  getAccountAccessForRole,
  getPermissionDescriptions,
  getExternalAccountSubtitle,
  translatePermissionFtlIds,
  hasAnyWritePermissionForLimits,
  hasWritePermissionForModels,
  DEFAULT_ROLE_NAMES,
  LIMITS_DATA,
} from "./OrganizationRoles.helpers";
import AccountTile from "./AccountTile";
import LongRoleTile from "./LongRoleTile";
import styles from "./OrganizationRoles.module.scss";

interface RoleDetailsDrawerProps {
  role: Role | null;
  isOpen: boolean;
  onClose: () => void;
  onSelectRole: (role: Role) => void;
  organizationUsers?: OrganizationUser[];
  allAccounts?: (API.Account | API.ExternalAccount)[];
  orgLimits?: API.Limits.ByType;
  hasInstitutionLoaded?: boolean;
}

interface ToggleAccountsButtonProps {
  isExpanded: boolean;
  onToggle: () => void;
  expandedLabel: string;
  collapsedLabel: string;
}

function ToggleAccountsButton({
  isExpanded,
  onToggle,
  expandedLabel,
  collapsedLabel,
}: ToggleAccountsButtonProps) {
  return (
    <div style={{ marginTop: "16px" }}>
      <Button
        kind="plain"
        onClick={onToggle}
        endIcon={isExpanded ? "chevron-up" : "chevron-down"}
      >
        {isExpanded ? expandedLabel : collapsedLabel}
      </Button>
    </div>
  );
}

export function RoleDetailsDrawer({
  role,
  isOpen,
  onClose,
  onSelectRole,
  organizationUsers,
  allAccounts,
  orgLimits,
  hasInstitutionLoaded,
}: RoleDetailsDrawerProps) {
  const { l10n } = useLocalization();
  const [showAllAccounts, setShowAllAccounts] = useState(false);
  const [showAdminPermissions, setShowAdminPermissions] = useState(false);
  const [showAllPermissions, setShowAllPermissions] = useState(false);

  useEffect(() => {
    setShowAllAccounts(false);
    setShowAdminPermissions(false);
    setShowAllPermissions(false);
  }, [role?.uuid]);

  if (!role) {
    return null;
  }

  const usersForRole = getUsersForRole(role.uuid, organizationUsers);
  const isDefault = DEFAULT_ROLE_NAMES.has(role.name);
  const { accounts } = getAccountAccessForRole(role, allAccounts);
  const isAdmin = role.name === OrganizationUser.ROLE.ADMIN;

  const hasMoreThanFourAccounts = accounts.length > 4;
  const displayedAccounts = showAllAccounts ? accounts : accounts.slice(0, 4);
  const permissions = getPermissionDescriptions(role.permissions);

  // Compute dynamic subtitle for "Make external transfer" permission
  const externalTransferSubtitle = getExternalAccountSubtitle(
    role,
    allAccounts
  );

  const permissionsWithDynamicSubtitle = permissions.map((perm) =>
    perm.modelStr === MODEL_STR.ACH_TRANSFER && externalTransferSubtitle
      ? { ...perm, subtitleFtlId: externalTransferSubtitle }
      : perm
  );

  const limits: API.Limits.ByType =
    Object.keys(role.limits).length > 0
      ? (role.limits as API.Limits.ByType)
      : orgLimits ?? {};

  const hasPermissionForAnyLimits = hasAnyWritePermissionForLimits(
    role.permissions,
    LIMITS_DATA
  );

  const handleSelectRole = () => {
    onSelectRole(role);
    onClose();
  };

  return (
    <Drawer
      isOpen={isOpen}
      onUserDismiss={onClose}
      depth="630px"
      paddingSize="l"
      showControls={false}
      footer={
        <div
          style={{ display: "flex", gap: "12px", justifyContent: "flex-end" }}
        >
          <Button kind="secondary" onClick={onClose}>
            {l10n.getString("new-user-drawer-close")}
          </Button>
          <Button kind="primary" onClick={handleSelectRole}>
            {l10n.getString("new-user-drawer-select-role")}
          </Button>
        </div>
      }
    >
      <div style={{ paddingBottom: "20px" }}>
        <div style={{ marginBottom: "12px" }}>
          <Row gapSize="xs" alignItems="center">
            <h2 style={{ margin: 0 }}>{role.name}</h2>
            {isDefault && (
              <Chip
                isLabelVisible={false}
                startIcon="lock"
                label={l10n.getString("org-role-locked")}
              />
            )}
          </Row>
        </div>

        {role.description && (
          <p className={styles.roleDescription}>{role.description}</p>
        )}

        {usersForRole.length > 0 && (
          <>
            <p
              className={`${styles.usersSectionHeader} fontSize--xs fontWeight--bold`}
              style={{ marginTop: "24px", marginBottom: "12px" }}
            >
              {l10n.getString("org-role-assigned-to")}
            </p>
            <ContentCard kind="bordered" paddingSize="m">
              {usersForRole.map((user, index) => (
                <div key={user.uuid}>
                  <Row gapSize="s" alignItems="center">
                    <Tooltip text={user.user.email}>
                      <Avatar
                        label={user.fullName()}
                        kind="tonal"
                        initials={user.userInitials()}
                      />
                    </Tooltip>
                    {user.fullName()}
                  </Row>
                  {index !== usersForRole.length - 1 && (
                    <hr className={styles.limitsdivider} />
                  )}
                </div>
              ))}
            </ContentCard>
          </>
        )}

        <p
          className={`${styles.usersSectionHeader} fontSize--xs fontWeight--bold`}
          style={{ marginTop: "24px", marginBottom: "12px" }}
        >
          <Row gapSize="xs" alignItems="center">
            <div>{l10n.getString("org-role-accounts")}</div>
            <Chip
              count={String(accounts.length)}
              isLabelVisible={false}
              label=""
            />
          </Row>
        </p>
        <ContentCard kind="bordered" paddingSize="m">
          {!showAllAccounts && accounts.length > 0 && (
            <div>
              <div className={styles.accountsTileGrid}>
                {displayedAccounts.map((account) => (
                  <AccountTile key={account.id} account={account} />
                ))}
              </div>
              {hasMoreThanFourAccounts && (
                <ToggleAccountsButton
                  isExpanded={showAllAccounts}
                  onToggle={() => setShowAllAccounts(!showAllAccounts)}
                  collapsedLabel={l10n.getString("org-role-button-view-all")}
                  expandedLabel={l10n.getString("org-role-button-see-less")}
                />
              )}
            </div>
          )}

          {showAllAccounts && accounts.length > 0 && (
            <div>
              <div className={styles.accountsTileGrid}>
                {displayedAccounts.map((account) => (
                  <AccountTile key={account.id} account={account} />
                ))}
              </div>
              {hasMoreThanFourAccounts && (
                <ToggleAccountsButton
                  isExpanded={showAllAccounts}
                  onToggle={() => setShowAllAccounts(!showAllAccounts)}
                  collapsedLabel={l10n.getString("org-role-button-view-all")}
                  expandedLabel={l10n.getString("org-role-button-see-less")}
                />
              )}
            </div>
          )}
        </ContentCard>

        <p
          className={`${styles.usersSectionHeader} fontSize--xs fontWeight--bold`}
          style={{ marginTop: "24px", marginBottom: "12px" }}
        >
          <Row gapSize="xs" alignItems="center">
            <Row.Item>
              <div>{l10n.getString("org-role-permissions")}</div>
            </Row.Item>
            <Row.Item shrink>
              <div style={{ marginBottom: "var(--space-s)" }}>
                <Chip
                  label={l10n.getString("org-role-same-across-accounts")}
                  startIcon="check"
                />
              </div>
            </Row.Item>
          </Row>
        </p>
        {role.name === OrganizationUser.ROLE.VIEWER ? (
          <LongRoleTile
            iconClass="narmi-icon-eye"
            title={l10n.getString("org-role-viewer-title")}
            description={l10n.getString("org-role-viewer-description")}
          />
        ) : (
          <ContentCard kind="bordered" paddingSize="m">
            {isAdmin && (
              <>
                <LongRoleTile
                  iconClass="narmi-icon-check"
                  title={l10n.getString("org-role-admin-title")}
                  description={l10n.getString("org-role-admin-description")}
                />
                {showAdminPermissions &&
                  permissionsWithDynamicSubtitle.map((permission) => {
                    const { translatedTitle, translatedSubtitle } =
                      translatePermissionFtlIds(permission, l10n);
                    return (
                      <Row
                        key={translatedTitle}
                        className={styles.permissionsRow}
                      >
                        <span
                          className={`narmi-icon-check ${styles.accountsIcon}`}
                          aria-hidden="true"
                        />
                        <div>
                          <p
                            className={`fontSize--16px ${styles.permissionTitle}`}
                          >
                            {translatedTitle}
                          </p>
                          <p
                            className={`fontSize--14px ${styles.permissionSubtitle}`}
                          >
                            {translatedSubtitle}
                          </p>
                        </div>
                      </Row>
                    );
                  })}
                <ToggleAccountsButton
                  isExpanded={showAdminPermissions}
                  onToggle={() =>
                    setShowAdminPermissions(!showAdminPermissions)
                  }
                  collapsedLabel={l10n.getString(
                    "org-role-button-view-permissions"
                  )}
                  expandedLabel={l10n.getString("org-role-button-see-less")}
                />
              </>
            )}

            {!isAdmin && role.name !== OrganizationUser.ROLE.VIEWER && (
              <>
                {permissionsWithDynamicSubtitle
                  .slice(0, 5)
                  .map((permission, index) => {
                    const { translatedTitle, translatedSubtitle } =
                      translatePermissionFtlIds(permission, l10n);
                    return (
                      <Row key={index} className={styles.permissionsRow}>
                        <span
                          className={`narmi-icon-check ${styles.accountsIcon}`}
                          aria-hidden="true"
                        />
                        <div>
                          <p
                            className={`fontSize--16px ${styles.permissionTitle}`}
                          >
                            {translatedTitle}
                          </p>
                          <p
                            className={`fontSize--14px ${styles.permissionSubtitle}`}
                          >
                            {translatedSubtitle}
                          </p>
                        </div>
                      </Row>
                    );
                  })}
                {showAllPermissions &&
                  permissionsWithDynamicSubtitle
                    .slice(5)
                    .map((permission, index) => {
                      const { translatedTitle, translatedSubtitle } =
                        translatePermissionFtlIds(permission, l10n);
                      return (
                        <Row key={index + 5} className={styles.permissionsRow}>
                          <span
                            className={`narmi-icon-check ${styles.accountsIcon}`}
                            aria-hidden="true"
                          />
                          <div>
                            <p
                              className={`fontSize--16px ${styles.permissionTitle}`}
                            >
                              {translatedTitle}
                            </p>
                            <p
                              className={`fontSize--14px ${styles.permissionSubtitle}`}
                            >
                              {translatedSubtitle}
                            </p>
                          </div>
                        </Row>
                      );
                    })}
                {permissionsWithDynamicSubtitle.length > 5 && (
                  <ToggleAccountsButton
                    isExpanded={showAllPermissions}
                    onToggle={() => setShowAllPermissions(!showAllPermissions)}
                    collapsedLabel={l10n.getString("org-role-button-view-all")}
                    expandedLabel={l10n.getString("org-role-button-see-less")}
                  />
                )}
              </>
            )}
          </ContentCard>
        )}

        {hasInstitutionLoaded &&
          Object.keys(limits).length > 0 &&
          hasPermissionForAnyLimits && (
            <>
              <div
                className={`${styles.usersSectionHeader} fontSize--xs fontWeight--bold`}
                style={{ marginTop: "24px", marginBottom: "12px" }}
              >
                {isAdmin
                  ? l10n.getString("org-role-organization-limits")
                  : l10n.getString("org-role-limits")}
              </div>
              <ContentCard kind="bordered" paddingSize="m">
                {isAdmin && (
                  <p
                    className={"fontSize--s"}
                    style={{
                      color: "var(--nds-color-gray-medium)",
                      marginBottom: "16px",
                    }}
                  >
                    {l10n.getString("org-role-organization-limits-subtitle")}
                  </p>
                )}
                {Object.entries(limits)
                  .filter(([limitType, limitValue]) => {
                    const limitData =
                      LIMITS_DATA[limitType as keyof typeof LIMITS_DATA];
                    const hasAnyPositiveLimit = Object.values(limitValue).some(
                      (v) => Number(v) > 0
                    );
                    return (
                      limitData &&
                      hasAnyPositiveLimit &&
                      hasWritePermissionForModels(
                        role.permissions,
                        limitData.modelStrs
                      )
                    );
                  })
                  .map(([limitType, limitValue], index, filteredArray) => {
                    const limitData =
                      LIMITS_DATA[limitType as keyof typeof LIMITS_DATA];
                    const sortedPeriods = Object.entries(limitValue)
                      .sort(
                        ([periodA], [periodB]) =>
                          Number(periodA) - Number(periodB)
                      )
                      .filter(([, amount]) => Number(amount) > 0);

                    return (
                      <div key={limitType}>
                        <Row>
                          <Row.Item>
                            <p>{l10n.getString(limitData.ftlId)}</p>
                          </Row.Item>
                          <Row.Item shrink>
                            <div style={{ textAlign: "right" }}>
                              {sortedPeriods.map(([period, amount]) => {
                                const periodLabel = l10n.getString(
                                  "org-role-limit-period-per-days",
                                  { days: Number(period) }
                                );
                                return (
                                  <p
                                    key={period}
                                    style={
                                      period !== "1"
                                        ? { color: "var(--color-mediumGrey)" }
                                        : undefined
                                    }
                                  >
                                    {formatNumber(Number(amount), "currency")}{" "}
                                    {periodLabel}
                                  </p>
                                );
                              })}
                            </div>
                          </Row.Item>
                        </Row>
                        {index !== filteredArray.length - 1 && (
                          <hr className={styles.limitsdivider} />
                        )}
                      </div>
                    );
                  })}
              </ContentCard>
            </>
          )}
      </div>
    </Drawer>
  );
}

export default RoleDetailsDrawer;
